import { Injectable } from '@angular/core';

import { OddsFormat } from '@bpos';
import { PicksView } from '@cds/betting-offer/domain-specific';
import { PicksRequest } from '@cds/query-objects';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { OddsOperations } from '@frontend/sports/odds/feature';
import { UserOddsFormatSettingsService } from '@frontend/sports/user/feature';
import { createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { OperatorFunction, filter, interval, mergeMap, pairwise, pipe, startWith, switchMap, takeUntil } from 'rxjs';

import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2Pick } from '../../../core/picks/betslip-v2-pick';
import { PickOddsState, PickUpdateType } from '../../../core/picks/pick-models';
import PickPushUpdaterService from '../../../integration/services/push-updaters/pick-push-updater.service';
import BetslipPickActions from '../actions';
import BetslipPickFactory from '../factory/betslip-pick.factory';
import { selectBetslipNonBetBuilderPicksCount, selectNonBetBuilderPicks } from '../selectors';
import { BetslipPicksRequestBuilderService } from '../services/betslip-pick-request-builder.service';

function filterFirstNonBetBuilderPickAdded(): OperatorFunction<number, [number, number]> {
    return pipe(
        startWith(0), // in case we're having a pick in the state after a refresh, we still want the pairwise/filter below to let this through
        pairwise(), // for some weird reason distinctUntilChanged acts weirdly when we remove all pick and then add one(prev is 1 instead of the expected 0)
        filter(([prev, curr]) => prev === 0 && curr > 0), // we want to let this through when going from 0 to at least 1 pick
    );
}

@Injectable({ providedIn: 'root' })
export default class PicksRefreshEffect {
    constructor(
        private readonly store: Store,
        private readonly betslipConfig: BetslipConfig,
        private readonly bettingOfferApi: BettingOfferApi,
        private readonly pickRequestBuilder: BetslipPicksRequestBuilderService,
        private readonly pickFactory: BetslipPickFactory,
        private readonly pickPushUpdaterService: PickPushUpdaterService,
        private readonly userOddsFormatSettingsService: UserOddsFormatSettingsService,
    ) {}

    get userFormat(): OddsFormat {
        return OddsOperations.convertToBposOddsFormat(this.userOddsFormatSettingsService.oddsFormat);
    }

    updatePicksHandler$ = createEffect(() =>
        this.store.select(selectBetslipNonBetBuilderPicksCount).pipe(
            filter(() => !!this.betslipConfig.cdsPicksPollingInterval),
            filterFirstNonBetBuilderPickAdded(),
            switchMap(() =>
                interval(this.betslipConfig.cdsPicksPollingInterval).pipe(
                    concatLatestFrom(() => this.store.select(selectNonBetBuilderPicks)),
                    filter(([_, picks]) => !!picks.length),
                    mergeMap(([_, picks]) => {
                        const request = this.pickRequestBuilder.buildRequestFromPicks(picks);
                        this.cleanUpGroupInfo(request);
                        const self = this;

                        return this.bettingOfferApi.getPicksViewCache(request).pipe(
                            filter((response): response is PicksView => !!response?.fixturePage?.fixtures),
                            mergeMap(function* (response: PicksView): Iterable<Action> {
                                yield* self.getUpdatePicksActions(response, picks);
                            }),
                        );
                    }),
                    // we want to stop polling when there are no non-betbuilder picks(sportcast)
                    takeUntil(this.store.select(selectNonBetBuilderPicks).pipe(filter((nonBetBuilderPicks) => !nonBetBuilderPicks.length))),
                ),
            ),
        ),
    );

    private *getUpdatePicksActions(response: PicksView, picks: BetslipPick[]): Iterable<Action> {
        const v1PicksToUpdate: { pick: BetslipV1Pick; updateType: PickUpdateType }[] = [];
        const v2PicksToUpdate: { pick: BetslipV2Pick; updateType: PickUpdateType }[] = [];
        for (const pick of picks) {
            const fixture = response.fixturePage.fixtures.find((current) => current.id === pick.id.fixtureId);

            let updatedPick = fixture ? this.pickFactory.createNewPickFromOldAndBcdPick(pick, pick.id, fixture) : null;

            if (!updatedPick || this.isOddStateClosed(updatedPick)) {
                updatedPick ??= pick.copy();
                updatedPick.market.isVisible = false;
            }

            const updateType = this.pickPushUpdaterService.findUpdateType(pick, updatedPick);
            // we're ignoring updates that don't affect odds/visibility. If there are no price/visibility changes, the updateType will be Unknown
            // If we don't skip it, we will end-up dispatching updateVXPicks actions for all pick on every poll. It's also not the goal for this initiative
            if (updateType !== PickUpdateType.None && updateType !== PickUpdateType.Unknown) {
                if (BetslipV1Pick.isPick(updatedPick)) {
                    v1PicksToUpdate.push({ pick: updatedPick, updateType });
                }
                if (BetslipV2Pick.isPick(updatedPick)) {
                    v2PicksToUpdate.push({ pick: updatedPick, updateType });
                }
            }

            this.tryUpdatePrice(pick, updatedPick);
        }

        if (v1PicksToUpdate.length) {
            yield BetslipPickActions.updateV1Picks({ pickUpdates: v1PicksToUpdate });
        }
        if (v2PicksToUpdate.length) {
            yield BetslipPickActions.updateV2Picks({ pickUpdates: v2PicksToUpdate });
        }
    }

    private isOddStateClosed(pick: BetslipPick) {
        return pick.oddsState === PickOddsState.Closed;
    }

    private tryUpdatePrice(pick: BetslipPick, updatedPick: BetslipPick) {
        if (!updatedPick.currentPrice) {
            return;
        }

        // updating price visibility for the case when odds are below min
        if (updatedPick.currentPrice.nativeOdds.decimals <= 1) {
            updatedPick.currentPrice.isVisible = false;
        }

        // updating price history in case value or visibility changed
        if (
            !OddsOperations.equal(updatedPick.currentPrice.nativeOdds, pick.currentPrice!.nativeOdds, this.userFormat) ||
            updatedPick.currentPrice.isVisible !== pick.currentPrice!.isVisible
        ) {
            updatedPick.priceHistory = [...pick.priceHistory, { ...pick.currentPrice! }];
        }
    }

    private cleanUpGroupInfo(request: PicksRequest) {
        request.tv1Picks.forEach((pick) => {
            delete pick.pickGroupId;
        });
        request.tv2Picks.forEach((pick) => {
            delete pick.pickGroupId;
        });
    }
}
